import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from 'src/app/utils/notification.service';

@Component({
  selector: 'app-canvas-draw',
  templateUrl: './canvas-draw.component.html',
  styleUrls: ['./canvas-draw.component.css']
})
export class CanvasDrawComponent {
  @Input() title: string = $localize`:@@add_signature:Add Signature`;
  @Input() identifier: string = "";
  @Output() getImageData = new EventEmitter<any>();
  @Output() clearImageData = new EventEmitter<any>();

  // The canvas sits behind *ngIf="activeTab === 'draw'", so it's destroyed and
  // recreated every time the user switches tabs. Re-running init() from this
  // setter (instead of only once in ngAfterViewInit) re-attaches the drawing
  // listeners to the fresh element each time it reappears.
  private can: ElementRef<HTMLCanvasElement>;
  @ViewChild('can') set canvasRef(ref: ElementRef<HTMLCanvasElement> | undefined) {
    if (!ref) return;
    this.can = ref;
    this.init();
  }

  private canvas: HTMLCanvasElement;
  private ctx;
  private flag = false;
  private prevX = 0;
  private currX = 0;
  private prevY = 0;
  private currY = 0;
  private dot_flag = false;
  private x = "black";
  private y = 2;

  public activeTab: 'draw' | 'upload' = 'draw';
  public uploadedImage: string | null = null;
  private readonly maxUploadSizeMB = 2;
  private readonly acceptedUploadTypes = ['image/jpeg', 'image/png', 'image/bmp'];

  constructor(
    private dialogRef: MatDialogRef<CanvasDrawComponent>,
    private notif: NotificationService,
  ) {}

  setTab(tab: string) {
    this.activeTab = tab as 'draw' | 'upload';
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!this.acceptedUploadTypes.includes(file.type)) {
      this.notif.error($localize`:@@unsupported_file_format:Unsupported file format`);
      input.value = '';
      return;
    }
    if (file.size > this.maxUploadSizeMB * 1024 * 1024) {
      this.notif.error($localize`:@@file_should_not_exceed_2mb:File should not exceed 2MB`);
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.uploadedImage = e.target.result;
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  init() {
    this.canvas = this.can.nativeElement;
    this.ctx = this.canvas.getContext("2d");

    // On Pen Move
    // touchmove/touchstart need { passive: false } for preventDefault() below to
    // actually take effect — browsers otherwise treat touch listeners as passive
    // by default and silently ignore it, leaving the page free to scroll instead
    // of the canvas being drawn on.
    this.canvas.addEventListener("touchmove", (e) => this.findxy("move", e), { passive: false });
    this.canvas.addEventListener("mousemove", (e) => this.findxy("move", e));
    // On Pen Down
    this.canvas.addEventListener("touchstart", (e) => this.findxy("down", e), { passive: false });
    this.canvas.addEventListener("mousedown", (e) => this.findxy("down", e));
    // On Pen Up
    this.canvas.addEventListener("touchend", (e) => this.findxy("up", e));
    this.canvas.addEventListener("mouseup", (e) => this.findxy("up", e));
    this.canvas.addEventListener("mouseout", (e) => this.findxy("out", e));
  }
  findxy(res, e) {
    // A TouchEvent has no clientX/clientY of its own — those live on its
    // touches/changedTouches list. Falling back to `e` covers MouseEvent, which
    // does carry them directly.
    const point = e.touches?.[0] ?? e.changedTouches?.[0] ?? e;
    if (e.touches || e.changedTouches) {
      // Stop the default touch behavior (page scroll/pinch-zoom) so touching the
      // canvas draws instead of panning the page.
      e.preventDefault();
    }

    if (res == "down") {
      this.prevX = this.currX;
      this.prevY = this.currY;
      this.currX = point.clientX - this.canvas.getBoundingClientRect().left;
      this.currY = point.clientY - this.canvas.getBoundingClientRect().top;

      this.flag = true;
      this.dot_flag = true;
      if (this.dot_flag) {
        this.ctx.beginPath();
        this.ctx.fillStyle = this.x;
        this.ctx.fillRect(this.currX, this.currY, 0, 0);
        this.ctx.closePath();
        this.dot_flag = false;
      }
    }
    if (res == "up" || res == "out") {
      this.flag = false;
    }
    if (res == "move") {
      if (this.flag) {
        this.prevX = this.currX;
        this.prevY = this.currY;
        this.currX = point.clientX - this.canvas.getBoundingClientRect().left;
        this.currY = point.clientY - this.canvas.getBoundingClientRect().top;
        this.draw();
      }
    }
  }
  draw() {
    this.ctx.beginPath();
    this.ctx.moveTo(this.prevX, this.prevY);
    this.ctx.lineTo(this.currX, this.currY);
    this.ctx.strokeStyle = this.x;
    this.ctx.lineWidth = this.y;
    this.ctx.stroke();
    this.ctx.closePath();
  }

  save() {
    if (this.activeTab === 'upload') {
      if (!this.uploadedImage) {
        this.clear();
        this.dialogRef.close();
        return;
      }
      this.dialogRef.close(this.uploadedImage);
      return;
    }

    // const canvasImg = document.getElementById("canvasimg") as HTMLImageElement;

    // canvasImg.style.border = "2px solid";
    this.canvas.style.width = "150px";
    this.canvas.style.height = "100px";
    const dataURL = this.canvas.toDataURL();




    if (this.isCanvasBlank(this.canvas)) {
      this.clear();
      this.dialogRef.close();
      return
    }

    // canvasImg!.src = dataURL;
    // document.getElementById("canvasimg").style.display = "inline";
    this.reset();
    this.canvas.style.width = "300px";
    this.canvas.style.height = "150px";

    this.dialogRef.close(dataURL)
  }

  reset() {
    const w = this.canvas.width;
    const h = this.canvas.height;
    this.ctx.clearRect(0, 0, w, h);
  }

  private isCanvasBlank(canvas) {
    const context = canvas.getContext("2d");

    const pixelBuffer = new Uint32Array(
      context.getImageData(0, 0, canvas.width, canvas.height).data.buffer
    );

    return !pixelBuffer.some((color) => color !== 0);
  }

  clear() {
    if (this.activeTab === 'upload') {
      this.uploadedImage = null;
      this.clearImageData.emit();
      return;
    }
    this.reset();
    this.clearImageData.emit();
  }


}
